package check

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlin.math.max
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import debug.BUILD
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.events.Event
import puzzlekey.Bundle
import puzzlekey.Envelope
import puzzlekey.OpenResult
import puzzlekey.Projector
import puzzlekey.QrSecret
import puzzlekey.Radar
import puzzlekey.RecentEmbeddings
import puzzlekey.SealedBundle
import puzzlekey.radarScore
import radar.Sparkles
import scan.CameraStage
import scan.SCAN_INTERVAL_MS
import scan.Scanner
import scan.cameraEnded
import scan.describeCameraError
import scan.loadCentre
import scan.loadScanModel
import scan.loadSealed

/** Frames kept in the ✓/✗ strip. */
private const val HISTORY = 30

/** How one frame's candidates came out. Only this is kept: never the decrypted Bundle. */
private enum class Outcome(val glyph: String) {
    OPENED("✓"),
    OPENED_BUT_NOT_A_BUNDLE("⚠"),
    DAMAGED("⚠"),
    NOT_OPENED("·"),

    /** The Radar said cold, so its candidates didn't run (ADR-0013 §4). */
    COLD("○"),
}

private fun outcomeOf(result: OpenResult): Outcome = when (result) {
    is OpenResult.Opened -> if (Bundle.fromBytes(result.bundle) != null) Outcome.OPENED else Outcome.OPENED_BUT_NOT_A_BUNDLE
    OpenResult.Damaged -> Outcome.DAMAGED
    OpenResult.WrongKey -> Outcome.NOT_OPENED
}

/**
 * One frame's check: its Scan, its Radar [score] (and the [smoothed] one), then its candidates against the
 * Puzzle wrap unless cold. [number] counts from 1 since the last reset.
 */
private class FrameCheck(
    val number: Int,
    val outcome: Outcome,
    val tried: Int,
    val scanMs: Double,
    val kdfMs: Double,
    val maskedFlips: Int?,
    val score: Double,
    val smoothed: Double,
)

/**
 * Check mode (T13, CONTEXT.md): `/?check#<QR secret>`. Scans as the unlock will, tries each frame's
 * candidates under `sealed.json`'s Error tolerance against the real Puzzle wrap, and reports
 * "✓ opened after N candidates" without the reveal. It has nothing to show bit errors against: the
 * reference never reaches the site (ADR-0009 §2), so only whether a frame opens and how many masked
 * bits changed since the last frame. It also shows the Radar (T14, ADR-0013): the sparkles over the
 * video, the score, and the KDF skipped on cold frames.
 */
@Composable
fun CheckMode() {
    val errors = remember { mutableStateListOf<String>() }
    var status by remember { mutableStateOf("loading sealed.json and the model…") }
    var sealed by remember { mutableStateOf<SealedBundle?>(null) }
    val qrSecret = remember { QrSecret.parse(window.location.hash.removePrefix("#")) }
    var scanner by remember { mutableStateOf<Scanner?>(null) }
    var video by remember { mutableStateOf<HTMLVideoElement?>(null) }
    var cameraOn by remember { mutableStateOf(false) }
    var paused by remember { mutableStateOf(false) }
    var averaging by remember { mutableStateOf(1) }
    val history = remember { mutableStateListOf<FrameCheck>() }
    var frames by remember { mutableStateOf(0) }
    var opened by remember { mutableStateOf(0) }
    var lastOpened by remember { mutableStateOf<FrameCheck?>(null) }
    var radar by remember { mutableStateOf<Radar?>(null) }
    var warmth by remember { mutableStateOf(0.0) }

    DisposableEffect(Unit) {
        val onError: (Event) -> Unit = { e -> errors += "${e.asDynamic().message ?: e.type}" }
        val onRejection: (Event) -> Unit = { e -> errors += "${e.asDynamic().reason}" }
        window.addEventListener("error", onError)
        window.addEventListener("unhandledrejection", onRejection)
        onDispose {
            window.removeEventListener("error", onError)
            window.removeEventListener("unhandledrejection", onRejection)
        }
    }

    LaunchedEffect(Unit) {
        if (qrSecret == null) {
            status = "no QR secret: open /?check#<the text of assets/key.txt>"
            return@LaunchedEffect
        }
        try {
            val loaded = loadSealed() ?: error("sealed.json isn't a v${Envelope.VERSION} sealed Bundle: re-run seal")
            val shipped = loadCentre().model
            check(shipped == loaded.scan.model) { "sealed.json was enrolled with ${loaded.scan.model}, but the site ships $shipped: re-run seal" }
            val model = loadScanModel()
            scanner = Scanner(model, Projector(loaded.scan.centre, loaded.scan.seed))
            sealed = loaded
            radar = Radar(loaded.radar)
            status = tolerance(loaded)
        } catch (e: Throwable) {
            status = "not ready: ${e.message ?: e}"
        }
    }

    LaunchedEffect(scanner, cameraOn, paused) {
        val s = scanner ?: return@LaunchedEffect
        val v = video ?: return@LaunchedEffect
        val target = sealed ?: return@LaunchedEffect
        val secret = qrSecret ?: return@LaunchedEffect
        val r = radar ?: return@LaunchedEffect
        if (!cameraOn || paused) return@LaunchedEffect
        var previous: DoubleArray? = null
        while (isActive) {
            val start = window.performance.now()
            if (cameraEnded(v)) {
                cameraOn = false
                errors += "the camera stopped (tab in the background?): tap to start it again"
                break
            }
            try {
                val scan = s.scan(v)
                val score = radarScore(scan.projection, target.scan.mask)
                val smoothed = r.next(score)
                warmth = r.warmth
                val t0 = window.performance.now()
                // The candidates run back to back, longer than the Scan interval (ADR-0012), and only when
                // the frame could open (ADR-0013 §4): gated on its own score, not the smoothed one, which
                // lags and would skip the first frame that opens.
                val attempt = if (target.radar.worthTrying(score)) Envelope.openWithScan(target, secret, scan.projection) else null
                val kdfMs = window.performance.now() - t0
                val flips = previous?.let { p -> target.scan.mask.count { (scan.projection[it] > 0) != (p[it] > 0) } }
                previous = scan.projection
                frames++
                val outcome = attempt?.let { outcomeOf(it.result) } ?: Outcome.COLD
                val frame = FrameCheck(frames, outcome, attempt?.tried ?: 0, scan.timings.total, kdfMs, flips, score, smoothed)
                history += frame
                if (history.size > HISTORY) history.removeAt(0)
                if (frame.outcome == Outcome.OPENED) {
                    opened++
                    lastOpened = frame
                }
            } catch (e: Throwable) {
                errors += "check: ${e.message ?: e}"
            }
            delay(max(0.0, SCAN_INTERVAL_MS - (window.performance.now() - start)).toLong())
        }
    }

    CameraStage(
        cameraOn = cameraOn,
        onVideo = { video = it },
        onStarted = {
            scanner?.recent?.clear()
            radar?.reset()
            warmth = 0.0
            cameraOn = true
        },
        onError = { errors += describeCameraError(it) },
        overlay = { Sparkles { warmth } },
    )

    Div({ classes("panel") }) {
        Div({ classes("verdict") }) { Text(verdict(history.lastOrNull())) }
        Pre { Text(summary(status, history, frames, opened, lastOpened, averaging, radar)) }
        Div({ classes("strip") }) {
            Text(history.joinToString("") { it.outcome.glyph })
        }
        Div({ classes("row") }) {
            for (n in 1..3) {
                Button({
                    if (n == averaging) classes("on")
                    onClick {
                        averaging = n
                        scanner?.recent = RecentEmbeddings(n)
                        radar?.reset()
                        warmth = 0.0
                    }
                }) { Text(if (n == 1) "1 frame" else "avg $n") }
            }
        }
        Div({ classes("row") }) {
            Button({
                if (paused) classes("on")
                onClick { paused = !paused }
            }) { Text(if (paused) "Resume" else "Pause") }
            Button({
                onClick {
                    history.clear()
                    frames = 0
                    opened = 0
                    lastOpened = null
                }
            }) { Text("Reset counts") }
        }
        if (errors.isNotEmpty()) Pre({ classes("errors") }) { Text(errors.joinToString("\n") { "⚠ $it" }) }
        Div({ classes("build") }) { Text("build $BUILD") }
    }
}

private fun verdict(frame: FrameCheck?): String = when (frame?.outcome) {
    null -> "Point the camera at the Puzzle"
    Outcome.OPENED -> "✓ opened after ${frame.tried} candidates"
    Outcome.OPENED_BUT_NOT_A_BUNDLE -> "⚠ opened after ${frame.tried} candidates, but the Bundle doesn't parse"
    Outcome.DAMAGED -> "⚠ the key opened after ${frame.tried} candidates, but the Bundle is damaged"
    Outcome.NOT_OPENED -> "✗ not opened (${frame.tried} candidates)"
    Outcome.COLD -> "○ cold: score ${two(frame.score)}, candidates not tried"
}

private fun tolerance(s: SealedBundle) =
    "${s.scan.mask.size}-bit mask · maxErrors ${s.scan.maxErrors} · candidatesPerFrame ${s.scan.candidatesPerFrame} · " +
        "PBKDF2 × ${s.pbkdf2.iterations} · radar cold ${two(s.radar.cold)}, hot ${two(s.radar.hot)}"

private fun summary(
    status: String,
    history: List<FrameCheck>,
    frames: Int,
    opened: Int,
    lastOpened: FrameCheck?,
    averaging: Int,
    radar: Radar?,
): String = buildString {
    appendLine(status)
    appendLine("opened $opened of $frames frames · averaging $averaging")
    lastOpened?.let { appendLine("last ✓: frame ${it.number}, after ${it.tried} candidates") }
    val last = history.lastOrNull() ?: return@buildString
    if (radar != null) {
        val tried = history.count { it.outcome != Outcome.COLD }
        appendLine("radar: score ${two(last.score)} · smoothed ${two(last.smoothed)} · warmth ${two(radar.warmth)} · KDF ran on $tried of the last ${history.size}")
    }
    append("last frame: Scan ${ms(last.scanMs)} · candidates ${ms(last.kdfMs)} · masked bits changed since the previous frame: ${last.maskedFlips ?: "–"}")
}

private fun ms(value: Double) = "${value.toInt()} ms"

private fun two(value: Double) = value.asDynamic().toFixed(2) as String
